// CFPB portal login on the client overview. Mirrors MonitoringLoginCard: the
// login email is always visible and copyable; the password is revealed on
// demand (audited server-side as pii.revealed, field cfpb_credentials),
// auto-hides after 30 s, and can be copied. Before this card existed the CFPB
// password could be saved but never read back, so staff could not copy it.
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi } from "../lib/api";
import { CopyButton } from "./MonitoringLoginCard";

export function CfpbLoginCard({ clientId, loginEmail, hasPassword = false, onUpdate, autoHideMs = 30_000 }: {
  clientId: string;
  loginEmail: string | null;
  /** A password is saved even if no login email is (see has_cfpb_credentials). */
  hasPassword?: boolean;
  onUpdate: () => void;
  autoHideMs?: number;
}) {
  const [revealed, setRevealed] = useState<{ clientId: string; email: string | null; password: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  // Every reveal gets a sequence number; a response is applied only if it is
  // still the latest request for the client currently on screen. Switching
  // clients or unmounting bumps the number, so a late answer is discarded.
  const seq = useRef(0);

  function hide() {
    setRevealed(null);
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
  }
  useEffect(() => {
    seq.current += 1; hide(); setErr(null); setLoading(false);
    return () => { seq.current += 1; hide(); };
  }, [clientId]);

  async function reveal() {
    if (revealed) { hide(); return; }
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const v = await clientsApi.revealCfpb(clientId);
      if (mine !== seq.current) return; // stale: client changed or card unmounted
      setRevealed({ clientId, email: v?.email ?? null, password: v?.password ?? null });
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(hide, autoHideMs);
    } catch (e: any) {
      if (mine !== seq.current) return;
      setErr(e?.body?.message || e?.message || "Could not reveal");
    } finally { if (mine === seq.current) setLoading(false); }
  }

  const shown = revealed && revealed.clientId === clientId ? revealed : null;
  const email = shown?.email ?? loginEmail;

  if (!email && !hasPassword && !shown) {
    return (
      <div className="monitoring-login">
        <div className="monitoring-login__row">
          <span className="muted small">No CFPB login saved</span>
          <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={onUpdate}>Set up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoring-login">
      <div className="monitoring-login__row">
        <span className="muted small" style={{ minWidth: 68 }}>Login</span>
        <code className="monitoring-login__value">{email || "—"}</code>
        <CopyButton value={email} label="CFPB login" />
      </div>
      <div className="monitoring-login__row">
        <span className="muted small" style={{ minWidth: 68 }}>Password</span>
        <code className="monitoring-login__value">{shown ? (shown.password || "(none saved)") : "••••••••"}</code>
        <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={reveal} disabled={loading} title={shown ? "Hide" : "Reveal password (logged)"}>
          {loading ? <Loader2 size={12} className="spin" /> : shown ? <EyeOff size={12} aria-hidden="true" /> : <Eye size={12} aria-hidden="true" />} {shown ? "Hide" : "Reveal"}
        </button>
        {shown && <CopyButton value={shown.password} label="CFPB password" />}
      </div>
      <div className="monitoring-login__row">
        <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={onUpdate}>Update login</button>
        <span className="muted small">Saved password stays unless you type a new one.</span>
      </div>
      {err && <span className="revealable-error">{err}</span>}
    </div>
  );
}
